import React, { useState } from "react";
import { User, Shield, Bell, Heart, LogOut, Upload, Trash2, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  updateProfile,
  changePassword,
  updatePreferences,
  updateNotifications,
  uploadAvatar,
  deleteAccount,
} from "@/lib/api";
import { SETTINGS } from "@/constants/testIds";
import { toast } from "sonner";

const TOP_SIZES = ["S", "M", "L", "XL"];
const BOTTOM_SIZES = ["28", "30", "32", "34", "36"];
const CATEGORIES = ["Tops", "Bottoms", "Outerwear"];

const Toggle = ({ on, onChange, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={() => onChange(!on)}
    className={`relative w-12 h-6 border transition-colors ${
      on ? "bg-[#5B2A86] border-[#5B2A86]" : "bg-transparent border-border"
    }`}
  >
    <span
      className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-foreground transition-all ${
        on ? "left-[26px]" : "left-1"
      }`}
    />
  </button>
);

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <section className="border border-border p-6 md:p-8">
    <div className="flex items-center gap-3 mb-6 pb-6 border-b border-border">
      <Icon className="w-5 h-5 text-[#5B2A86]" strokeWidth={1.5} />
      <div>
        <h2 className="font-serif text-2xl uppercase tracking-tight leading-none">{title}</h2>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {subtitle}
        </p>
      </div>
    </div>
    {children}
  </section>
);

const labelCls = "text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground";
const inputCls =
  "w-full mt-2 bg-transparent border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-foreground";
const btnCls =
  "bg-foreground text-background px-6 py-3 text-xs font-mono uppercase tracking-[0.3em] hover:bg-[#5B2A86] hover:text-foreground transition-colors disabled:opacity-50";

const Settings = () => {
  const { user, loading, setUser, logout, openAuthModal } = useAuth();

  const [profile, setProfile] = useState({ name: "", email: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState("");
  const [prefs, setPrefs] = useState(null);

  React.useEffect(() => {
    if (user) setProfile({ name: user.name || "", email: user.email || "" });
  }, [user]);

  if (loading) {
    return (
      <div className="px-5 md:px-10 py-24 min-h-screen flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
          Loading…
        </p>
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid={SETTINGS.gate} className="px-5 md:px-10 py-14 min-h-screen">
        <div className="border-b border-border pb-8 mb-16">
          <h1 className="font-serif text-6xl sm:text-7xl uppercase tracking-tighter leading-none">
            Account
          </h1>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Manage your walk
          </p>
        </div>
        <div className="flex flex-col items-center justify-center text-center py-24 border border-border">
          <User className="w-10 h-10 text-[#5B2A86] mb-6" strokeWidth={1.5} />
          <h2 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight">
            Sign in required
          </h2>
          <p className="mt-4 mb-8 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground max-w-md">
            Enter or join the procession to manage your profile, preferences and wishlist.
          </p>
          <button data-testid={SETTINGS.gateSignIn} onClick={openAuthModal} className={btnCls}>
            Enter / Join
          </button>
        </div>
      </div>
    );
  }

  const isPasswordUser = user.auth_provider === "password";

  const saveProfile = async () => {
    setBusy("profile");
    try {
      const u = await updateProfile(profile);
      setUser(u);
      toast.success("Profile updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    } finally {
      setBusy("");
    }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("avatar");
    try {
      const res = await uploadAvatar(file);
      setUser({ ...user, picture: res.picture });
      toast.success("Avatar updated.");
    } catch {
      toast.error("Avatar upload failed.");
    } finally {
      setBusy("");
    }
  };

  const savePassword = async () => {
    setBusy("password");
    try {
      await changePassword(pwd);
      setPwd({ current_password: "", new_password: "" });
      toast.success("Password updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Password update failed.");
    } finally {
      setBusy("");
    }
  };

  const saveNotif = async (patch) => {
    const prev = user.notifications;
    setUser({ ...user, notifications: { ...prev, ...patch } });
    try {
      const u = await updateNotifications(patch);
      setUser(u);
    } catch {
      setUser({ ...user, notifications: prev });
      toast.error("Failed to save.");
    }
  };

  const currentPrefs = prefs || user.preferences;

  const toggleCategory = (cat) => {
    const cats = currentPrefs.categories || [];
    const next = cats.includes(cat) ? cats.filter((c) => c !== cat) : [...cats, cat];
    setPrefs({ ...currentPrefs, categories: next });
  };

  const savePrefs = async () => {
    setBusy("prefs");
    try {
      const u = await updatePreferences(currentPrefs);
      setUser(u);
      setPrefs(null);
      toast.success("Preferences saved.");
    } catch {
      toast.error("Failed to save preferences.");
    } finally {
      setBusy("");
    }
  };

  const removeAccount = async () => {
    setBusy("delete");
    try {
      await deleteAccount();
      setUser(null);
      toast.success("Account deleted.");
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid={SETTINGS.page} className="px-5 md:px-10 py-14 min-h-screen">
      <div className="border-b border-border pb-8 mb-12 flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 className="font-serif text-6xl sm:text-7xl uppercase tracking-tighter leading-none">
            Account
          </h1>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {user.email} · {user.auth_provider === "google" ? "Google" : "Email"}
          </p>
        </div>
        <button
          data-testid={SETTINGS.logoutButton}
          onClick={logout}
          className="flex items-center gap-2 border border-border px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] hover:border-foreground transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* PROFILE */}
        <Section icon={User} title="Profile" subtitle="Your identity">
          <div className="flex items-center gap-5 mb-6">
            <div className="w-20 h-20 border border-border overflow-hidden bg-secondary flex items-center justify-center shrink-0">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
              )}
            </div>
            <label
              data-testid={SETTINGS.avatarUpload}
              className="flex items-center gap-2 border border-border px-4 py-2 text-xs font-mono uppercase tracking-[0.2em] cursor-pointer hover:border-foreground transition-colors"
            >
              <Upload className="w-4 h-4" />
              {busy === "avatar" ? "Uploading…" : "Upload"}
              <input
                type="file"
                accept="image/*"
                data-testid={SETTINGS.avatarInput}
                onChange={onAvatar}
                className="hidden"
              />
            </label>
          </div>
          <div className="space-y-5">
            <div>
              <label className={labelCls}>Name</label>
              <input
                data-testid={SETTINGS.profileName}
                className={inputCls}
                value={profile.name}
                onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input
                type="email"
                data-testid={SETTINGS.profileEmail}
                className={inputCls}
                value={profile.email}
                onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
              />
            </div>
            <button
              data-testid={SETTINGS.profileSave}
              onClick={saveProfile}
              disabled={busy === "profile"}
              className={btnCls}
            >
              {busy === "profile" ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </Section>

        {/* SECURITY */}
        <Section icon={Shield} title="Security" subtitle="Password & account">
          {isPasswordUser ? (
            <div className="space-y-5 mb-8">
              <div>
                <label className={labelCls}>Current Password</label>
                <input
                  type="password"
                  data-testid={SETTINGS.currentPassword}
                  className={inputCls}
                  value={pwd.current_password}
                  onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))}
                />
              </div>
              <div>
                <label className={labelCls}>New Password</label>
                <input
                  type="password"
                  data-testid={SETTINGS.newPassword}
                  className={inputCls}
                  value={pwd.new_password}
                  onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))}
                />
              </div>
              <button
                data-testid={SETTINGS.passwordSave}
                onClick={savePassword}
                disabled={busy === "password"}
                className={btnCls}
              >
                {busy === "password" ? "Saving…" : "Change Password"}
              </button>
            </div>
          ) : (
            <p className="mb-8 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground leading-relaxed">
              You signed in with Google. Password management is handled by your Google account.
            </p>
          )}

          <div className="pt-6 border-t border-border">
            <p className={labelCls}>Danger zone</p>
            {!confirmDelete ? (
              <button
                data-testid={SETTINGS.deleteAccount}
                onClick={() => setConfirmDelete(true)}
                className="mt-4 flex items-center gap-2 border border-destructive text-destructive px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] hover:bg-destructive hover:text-foreground transition-colors"
              >
                <Trash2 className="w-4 h-4" /> Delete Account
              </button>
            ) : (
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  data-testid={SETTINGS.deleteConfirm}
                  onClick={removeAccount}
                  disabled={busy === "delete"}
                  className="bg-destructive text-foreground px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] disabled:opacity-50"
                >
                  {busy === "delete" ? "Deleting…" : "Confirm Delete"}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="border border-border px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] hover:border-foreground transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </Section>

        {/* NOTIFICATIONS */}
        <Section icon={Bell} title="Notifications" subtitle="How we reach you">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-sm uppercase tracking-[0.15em]">Email Alerts</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  Drops, restocks & events
                </p>
              </div>
              <Toggle
                testId={SETTINGS.notifEmail}
                on={user.notifications?.email_alerts}
                onChange={(v) => saveNotif({ email_alerts: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-sm uppercase tracking-[0.15em]">In-App</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  On-site messages & badges
                </p>
              </div>
              <Toggle
                testId={SETTINGS.notifInapp}
                on={user.notifications?.inapp}
                onChange={(v) => saveNotif({ inapp: v })}
              />
            </div>
          </div>
        </Section>

        {/* SHOPPING PREFERENCES */}
        <Section icon={Heart} title="Shopping Preferences" subtitle="Tailor your fit">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Top Size</label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid={SETTINGS.prefTopSize}>
                  {TOP_SIZES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setPrefs({ ...currentPrefs, top_size: currentPrefs.top_size === s ? "" : s })}
                      className={`w-10 h-10 border font-mono text-xs transition-colors ${
                        currentPrefs.top_size === s
                          ? "bg-foreground text-background border-foreground"
                          : "border-border hover:border-foreground"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls}>Bottom Size</label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid={SETTINGS.prefBottomSize}>
                  {BOTTOM_SIZES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setPrefs({ ...currentPrefs, bottom_size: currentPrefs.bottom_size === s ? "" : s })}
                      className={`w-10 h-10 border font-mono text-xs transition-colors ${
                        currentPrefs.bottom_size === s
                          ? "bg-foreground text-background border-foreground"
                          : "border-border hover:border-foreground"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className={labelCls}>Favourite Categories</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {CATEGORIES.map((c) => {
                  const active = (currentPrefs.categories || []).includes(c);
                  return (
                    <button
                      key={c}
                      data-testid={`${SETTINGS.prefCategory}-${c.toLowerCase()}`}
                      onClick={() => toggleCategory(c)}
                      className={`flex items-center gap-2 px-4 py-2 border font-mono text-xs uppercase tracking-[0.15em] transition-colors ${
                        active
                          ? "bg-[#5B2A86] border-[#5B2A86] text-foreground"
                          : "border-border hover:border-foreground"
                      }`}
                    >
                      {active && <Check className="w-3 h-3" />}
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-sm uppercase tracking-[0.15em]">Marketing</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  Occasional emails from the studio
                </p>
              </div>
              <Toggle
                testId={SETTINGS.prefMarketing}
                on={currentPrefs.marketing_opt_in}
                onChange={(v) => setPrefs({ ...currentPrefs, marketing_opt_in: v })}
              />
            </div>

            <button
              data-testid={SETTINGS.prefSave}
              onClick={savePrefs}
              disabled={busy === "prefs"}
              className={btnCls}
            >
              {busy === "prefs" ? "Saving…" : "Save Preferences"}
            </button>

            <div className="pt-6 border-t border-border" data-testid={SETTINGS.wishlist}>
              <p className={labelCls}>Wishlist</p>
              {user.wishlist && user.wishlist.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-3">
                  {user.wishlist.map((slug) => (
                    <span
                      key={slug}
                      className="px-3 py-2 border border-border font-mono text-[10px] uppercase tracking-[0.15em]"
                    >
                      {slug.replace(/-/g, " ")}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Your wishlist is empty. Save pieces from any collection.
                </p>
              )}
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default Settings;
